import React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import { http } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ShareButton from "@/components/ShareButton";
import BackToTopButton from "@/components/BackToTopButton";
import { useAlertCount } from "@/components/AlertsPanel";
import { useSEO } from "@/hooks/useSEO";
import { scrollToPageTop } from "@/lib/scroll";
import { ArrowLeft, Loader2, Calendar, Tag } from "lucide-react";

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const alertCount = useAlertCount();
  React.useEffect(() => { scrollToPageTop(); }, [slug]);

  const q = useQuery({
    queryKey: ["blog", slug],
    queryFn: () => http.get(`/blog/${slug}`).then((r) => r.data),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  useSEO({
    title: q.data?.title
      ? `${q.data.title} — Popoyo.app Blog`
      : "Popoyo.app Blog",
    description: q.data?.excerpt || "Popoyo travel & surf guide from Popoyo.app.",
    // Prefer post cover, fall back to a dynamic branded OG card
    image: q.data?.cover_image_url || `https://popoyo.app/api/og/blog/${slug}.png`,
    path: `/blog/${slug}`,
  });

  if (q.isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header alertCount={alertCount} />
        <main className="flex-1 grid place-items-center">
          <Loader2 className="w-8 h-8 animate-spin text-[color:var(--coral)]" />
        </main>
        <Footer />
      </div>
    );
  }

  if (q.isError || !q.data) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header alertCount={alertCount} />
        <main className="flex-1 max-w-3xl mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: "Outfit" }}>Article not found</h1>
          <p className="text-[color:var(--ink-2)] mb-6">We couldn&apos;t find the article &quot;{slug}&quot;.</p>
          <button onClick={() => navigate("/blog")} className="pill px-4 py-2 bg-[color:var(--coral)] text-white">
            <ArrowLeft className="w-4 h-4 inline mr-1.5" /> Back to blog
          </button>
        </main>
        <Footer />
      </div>
    );
  }

  const p = q.data;

  return (
    <div className="min-h-screen flex flex-col" data-testid={`blog-post-${slug}`}>
      <Header alertCount={alertCount} />
      <main className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 space-y-6">
        <div className="flex items-center gap-2">
          <Link to="/blog" data-testid="blog-back" className="inline-flex items-center gap-1.5 text-sm text-[color:var(--ink-2)] hover:text-[color:var(--coral)]">
            <ArrowLeft className="w-4 h-4" /> All articles
          </Link>
          <div className="ml-auto">
            <ShareButton
              path={`/blog/${slug}`}
              title={`${p.title} — Popoyo.app`}
              text={p.excerpt || p.title}
              testId="share-blog-post-btn"
            />
          </div>
        </div>

        <article className="clay p-6 sm:p-8">
          <p className="eyebrow text-[color:var(--coral)]">{p.category || "Guide"}</p>
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-bold mt-1 leading-tight"
            style={{ fontFamily: "Outfit" }}
            data-testid="blog-post-title"
          >
            {p.title}
          </h1>
          <p className="text-xs text-[color:var(--ink-2)] mt-3 flex items-center gap-2 flex-wrap">
            <Calendar className="w-3 h-3" />
            {p.published_at && new Date(p.published_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
            {p.author && <><span>·</span><span>by {p.author}</span></>}
            {p.tags?.length > 0 && (
              <>
                <span>·</span>
                <Tag className="w-3 h-3" /> {p.tags.slice(0, 3).join(" · ")}
              </>
            )}
          </p>

          {p.cover_image_url && (
            <img
              src={p.cover_image_url}
              alt=""
              loading="eager"
              className="mt-5 rounded-2xl w-full object-cover max-h-[420px]"
            />
          )}

          {/* Markdown article body */}
          <div
            data-testid="blog-post-body"
            className="mt-6 prose max-w-none text-[color:var(--ink)] text-[15px] sm:text-base leading-relaxed"
            style={{ fontFamily: "'Work Sans', system-ui, sans-serif" }}
          >
            <ReactMarkdown
              components={{
                h1: ({ children }) => <h2 className="text-2xl sm:text-3xl font-bold mt-6 mb-3" style={{ fontFamily: "Outfit" }}>{children}</h2>,
                h2: ({ children }) => <h3 className="text-xl sm:text-2xl font-bold mt-6 mb-2" style={{ fontFamily: "Outfit" }}>{children}</h3>,
                h3: ({ children }) => <h4 className="text-lg font-bold mt-4 mb-2" style={{ fontFamily: "Outfit" }}>{children}</h4>,
                p:  ({ children }) => <p className="mb-3">{children}</p>,
                ul: ({ children }) => <ul className="list-disc list-outside pl-5 space-y-1 mb-4">{children}</ul>,
                ol: ({ children }) => <ol className="list-decimal list-outside pl-5 space-y-1 mb-4">{children}</ol>,
                a:  ({ href, children }) => <a href={href} className="text-[color:var(--coral)] underline hover:text-[color:var(--coral)]/80" target={href?.startsWith("http") ? "_blank" : undefined} rel={href?.startsWith("http") ? "noopener noreferrer" : undefined}>{children}</a>,
                blockquote: ({ children }) => <blockquote className="border-l-4 border-[color:var(--coral)] pl-4 italic text-[color:var(--ink-2)] my-4">{children}</blockquote>,
                code: ({ children }) => <code className="bg-[color:var(--sand-2)] px-1.5 py-0.5 rounded text-sm font-mono">{children}</code>,
              }}
            >
              {p.body_md}
            </ReactMarkdown>
          </div>
        </article>
      </main>
      <BackToTopButton />
      <Footer />
    </div>
  );
}
